import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  Mail,
  Lock,
  Loader2,
  Eye,
  EyeOff,
  ShieldCheck,
  Info,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/Login.css";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAdminNotice, setShowAdminNotice] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkUser = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session) {
        navigate("/admin/dashboard", { replace: true });
      }
    };
    checkUser();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password: password,
    });

    if (error) {
      setLoading(false);
      alert(error.message);
    } else if (data.user) {
      navigate("/admin/dashboard", { replace: true });
    }
  };

  return (
    <div className="portal-page-container">
      <div className="portal-mobile-bg-layer"></div>

      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className="portal-card"
      >
        <div className="branding-section">
          <div className="branding-overlay"></div>
          <div className="branding-inner-content">
            <div className="branding-top">
              <div className="branding-logo">
                <ShieldCheck size={24} strokeWidth={2.5} color="#60a5fa" />
                <span>RIFT NAKURU</span>
              </div>
            </div>
            <div className="branding-middle">
              <h2>
                Admin
                <br />
                Gateway
              </h2>
              <p>
                Secure administrative access to policy and claims databases.
              </p>
            </div>
            <div className="branding-bottom">
              <p className="security-tag">System Status: Encrypted</p>
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="form-container">
            <header className="form-header">
              <h1>Sign In</h1>
              <p>Enter your administrator credentials.</p>
            </header>

            <form onSubmit={handleLogin} className="login-form">
              <div className="input-field-group">
                <label>Email Address</label>
                <div className="input-wrapper">
                  <Mail size={16} className="input-icon" />
                  <input
                    type="email"
                    value={email}
                    placeholder="admin@riftnakuru.com"
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="input-field-group">
                <div className="label-split">
                  <label>Password</label>
                  <span
                    className="forgot-text"
                    onClick={() => setShowAdminNotice(true)}
                  >
                    Reset?
                  </span>
                </div>
                <div className="input-wrapper">
                  <Lock size={16} className="input-icon" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="password-toggle"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <AnimatePresence>
                {showAdminNotice && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="admin-notice-box"
                  >
                    <div className="notice-content">
                      <Info size={16} />
                      <p>
                        <strong>Security Policy:</strong> Password changes are
                        restricted. Please contact the ICT Department or System
                        Administrator to reset your credentials.
                      </p>
                    </div>
                    <div className="notice-actions">
                      <button
                        type="button"
                        onClick={() => setShowAdminNotice(false)}
                      >
                        Dismiss
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                type="submit"
                className="submit-action-btn"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="spinning-icon" size={18} />
                ) : (
                  "Unlock Dashboard"
                )}
              </button>
            </form>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
